import { css } from 'lit';

export const badgeStyles = css`
  :host {
    position: relative;
    display: inline-block;
    overflow: visible;
    font-family: var(--token-global-font-font-family-brand, 'Nunito Sans');
  }
  .badge-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    white-space: nowrap;
    pointer-events: none;
    font-weight: var(--token-global-font-font-weight-bold, 700);
    line-height: 12px;
    box-sizing: border-box;
    letter-spacing: 0.12px;
    gap: var(--token-global-spacing-s2, 2px);
    min-width: var(--token-global-spacing-s24, 24px);
    height: 20px;
    font-size: var(--token-global-font-font-size-xxxs, 12px);
    padding: var(--token-global-spacing-s4, 4px) var(--token-global-spacing-s6, 6px);
    background-color: var(--token-component-badge-informative-subtle-background, light-dark(lch(36.7 58.2 286 / 0.12), lch(77.7 28.7 275 / 0.12)));
    color: var(--token-component-badge-informative-subtle-text, light-dark(#3451b2, #aec0f5));
    --badge-icon-color: currentColor;
  }

  :host(.with-icon) .badge-content {
    position: absolute;
    bottom: 100%;
    left: 100%;
    margin: -12px;
  }
  
  /* Color variants */
  .badge-content.informative.subtle { 
    background: var(--token-component-badge-informative-subtle-background, light-dark(lch(36.7 58.2 286 / 0.12), lch(77.7 28.7 275 / 0.12)));
    color: var(--token-component-badge-informative-subtle-text, light-dark(#3451b2, #aec0f5));
  }
  .badge-content.informative.solid {
    background: var(--token-component-badge-informative-accent-background, light-dark(#3451b2, #aec0f5));
    color: var(--token-component-badge-informative-accent-text, light-dark(#ffffff, #343438));
  }
  .badge-content.warning.subtle {
    background: var(--token-component-badge-warning-subtle-background, light-dark(lch(67.2 71.6 80.1 / 0.12), lch(79.6 53.6 74.4 / 0.12)));
    color: var(--token-component-badge-warning-subtle-text, light-dark(#c25d05, #f2b67b));
  }
  .badge-content.warning.solid {
    background: var(--token-component-badge-warning-accent-background, light-dark(#c25d05, #f2b67b));
    color: var(--token-component-badge-warning-accent-text, light-dark(#ffffff, #343438));
  }
  .badge-content.positive.subtle {
    background: var(--token-component-badge-positive-subtle-background, light-dark(lch(47.6 47.7 150 / 0.12), lch(78.2 32.7 144 / 0.12)));
    color: var(--token-component-badge-positive-subtle-text, light-dark(#138246, #97cf9c));
  }
  .badge-content.positive.solid {
    background: var(--token-component-badge-positive-accent-background, light-dark(#138246, #97cf9c));
    color: var(--token-component-badge-positive-accent-text, light-dark(#ffffff, #343438));
  }
  .badge-content.negative.subtle {
    background: var(--token-component-badge-negative-subtle-background, light-dark(lch(41.2 69 32.4 / 0.12), lch(77.8 28 21.1 / 0.12)));
    color: var(--token-component-badge-negative-subtle-text, light-dark(#b82329, #f3aeaf));
  }
  .badge-content.negative.solid {
    background: var(--token-component-badge-negative-accent-background, light-dark(#b82329, #f3aeaf));
    color: var(--token-component-badge-negative-accent-text, light-dark(#ffffff, #343438));
  }
  .badge-content.important.subtle {
    background: var(--token-component-badge-important-subtle-background, light-dark(lch(38.2 75 307 / 0.12), lch(77.5 31.1 309 / 0.12)));
    color: var(--token-component-badge-important-subtle-text, light-dark(#753bbd, #d3b4ed));
  }
  .badge-content.important.solid {
    background: var(--token-component-badge-important-accent-background, light-dark(#753bbd, #d3b4ed));
    color: var(--token-component-badge-important-accent-text, light-dark(#ffffff, #343438));
  }
  .badge-content.pink.subtle {
    background: var(--token-component-badge-pink-subtle-background, light-dark(lch(47.1 72.1 347 / 0.12), lch(77.6 29.9 340 / 0.12)));
    color: var(--token-component-badge-pink-subtle-text, light-dark(#cd1d8d, #ecadd4));
  }
  .badge-content.pink.solid {
    background: var(--token-component-badge-pink-accent-background, light-dark(#cd1d8d, #ecadd4));
    color: var(--token-component-badge-pink-accent-text, light-dark(#ffffff, #343438));
  }
  .badge-content.neutral.subtle {
    background: var(--token-component-badge-neutral-subtle-background, light-dark(lch(34.6 3.66 286 / 0.12), lch(78.6 4.18 286 / 0.12)));
    color: var(--token-component-badge-neutral-subtle-text, light-dark(#515157, #c2c2ca));
  }
  .badge-content.neutral.solid {
    background: var(--token-component-badge-neutral-accent-background, light-dark(#515157, #c2c2ca));
    color: var(--token-component-badge-neutral-accent-text, light-dark(#ffffff, #343438));
  }
  .badge-content.white {
    background: var(--token-global-color-neutrals-white, light-dark(#ffffff, #ffffff));
    color: var(--token-global-color-neutrals-100, light-dark(#343438, #343438));
  }
  .badge-content.recommendation.subtle { 
    background: var(--token-component-badge-recommendation-subtle-background, light-dark(lch(45.6 31.8 185 / 0.12), lch(78.2 23.3 184 / 0.12)));
    color: var(--token-component-badge-recommendation-subtle-text, light-dark(#067a6f, #8dcec3));
  }
  .badge-content.recommendation.solid {
    background: var(--token-component-badge-recommendation-accent-background, light-dark(#067a6f, #8dcec3));
    color: var(--token-component-badge-recommendation-accent-text, light-dark(#ffffff, #343438));
  }
  .beta.beta.badge-content {
    background: var(--token-global-color-neutrals-70, light-dark(#696970, #696970));
    color: var(--token-global-color-neutrals-white, light-dark(#ffffff, #ffffff));
  }
  .new.new.badge-content {
    background: var(--token-global-color-violet-70, light-dark(#7259d6, #7259d6));
    color: var(--token-global-color-neutrals-white, light-dark(#ffffff, #ffffff));
  }

  /* Numeric badges */
  .badge-content.numeric {
    background: var(--token-component-global-header-badge-background, light-dark(#d63c42, #d63c42));
    color: var(--token-component-global-header-badge-text, light-dark(#ffffff, #ffffff));
    border-radius: 40px !important;
    min-width: 20px;
    min-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    &.black {
      background-color: var(--token-global-color-neutrals-90, light-dark(#3d3d42, #3d3d42));
      color: var(--token-global-color-neutrals-white, light-dark(#ffffff, #ffffff));
    }
    &.white {
      background-color: var(--token-global-color-neutrals-white, light-dark(#ffffff, #ffffff));
      color: var(--token-global-color-neutrals-100, light-dark(#343438, #343438));
    }
  }

  .badge-content ::slotted([slot="template"]) {
    display: flex;
    align-items: center;
    gap: var(--token-global-spacing-s2, 2px);
  }
`;
